import {
    Form,
    Input,
    Row,
    Col,
    Switch,
    InputNumber,
    Select
} from 'antd';
import { useRef, useEffect, useState } from 'react';
import WebEditUniversal, { WebEditUniversalProps } from '../../system/WebEditUniversal';
import getList from '../../../utilsTsx/universal/getList';
import fetchApi from '../../../utilsTsx/components/system/fetchApi';
import EditableSelect from '../../../utilsTsx/components/hooks/EditableSelect';

/** 主题id 非法字符：除 数字/字母/中文 之外的所有字符（含 : / 空格 - _ 等） */
const TOPIC_ID_INVALID_REG = /[^A-Za-z0-9\u4e00-\u9fa5]/g;
/** 主题id 合法格式：纯数字、字母、中文 */
const TOPIC_ID_PATTERN = /^[A-Za-z0-9\u4e00-\u9fa5]+$/;

/** MQTT 服务质量等级（QoS）：0-最多一次、1-至少一次、2-只有一次 */
const qosData = [
    { value: 0, label: '0 - 最多一次' },
    { value: 1, label: '1 - 至少一次' },
    { value: 2, label: '2 - 只有一次' }
];

const EditComponent = (props: any) => {
    const apiId = 'topic';
    const { defaultService } = props;
    const [form] = Form.useForm();
    const ref = useRef<any>(undefined);
    const [topicGroupId, setTopicGroupId] = useState<any>();

    useEffect(() => {
        fetchApi.GET(defaultService, {
            query: {
                apiId: 'topicGroup',
                apiExtend: 'showList'
            }
        }).then(res => {
            if (res.success) {
                setTopicGroupId(res.data);
            }
        });
    }, []);

    let extendProps: WebEditUniversalProps = {
        ...props,
        form,
        apiId,
        serviceId: 'mf',
        displayOptions: {
            width: 800
        },
        formOptions: {
            instance: form,
            children: (
                <Row gutter={[8, 0]}>
                    <Col span={6}>
                        <Form.Item
                            label="ID"
                            name="id"
                            tooltip="主题的唯一标识，由系统自动生成，创建后不可修改，通常仅用于系统内部关联与追溯。">
                            <Input disabled={true} />
                        </Form.Item>
                    </Col>

                    <Col span={8}>
                        <Form.Item
                            label="主题组"
                            name="topicGroupId">
                            <Select
                                allowClear
                                placeholder="请选择主题组"
                                showSearch={{
                                    optionFilterProp: 'children',
                                    filterOption: (input, option: any) =>
                                        option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                }}>
                                {getList.general(topicGroupId, { itemName: 'groupName', includeNull: true })}
                            </Select>
                        </Form.Item>
                    </Col>

                    <Col span={10}>
                        <Form.Item
                            label="主题名"
                            name="topicName"
                            tooltip="主题的展示名称，用于在列表、订阅关系中识别该主题，建议填写便于人工辨认的名称，如：设备上报、心跳。"
                            rules={[{ required: true, message: '主题名!' }]}>
                            <Input placeholder="如：设备上报、心跳" />
                        </Form.Item>
                    </Col>

                    <Col span={12}>
                        <Form.Item
                            label="主题"
                            name="topicStr"
                            // 过滤：只保留 数字 / 字母 / 中文，: / 等特殊字符无法保存
                            normalize={(value) => (value ?? '').replace(TOPIC_ID_INVALID_REG, '')}
                            tooltip="主题的业务标识（与副主题组成唯一约束），用于平台内部路由与匹配。
                            只能包含数字、字母、中文，不能包含 : / 等特殊字符，例如：print"
                            rules={[
                                { required: true, message: '主题只能包含数字、字母、中文，不能包含 : / 等特殊字符!' },
                                {
                                    pattern: TOPIC_ID_PATTERN,
                                    message: '只能输入数字、字母或中文，不能包含 : / 等特殊字符'
                                }
                            ]}>
                            <EditableSelect
                                defaultService={defaultService}
                                apiConfig={{ apiId: 'topicSelect' }}
                                placeholder="请选择或输入主题"
                            />
                        </Form.Item>
                    </Col>

                    <Col span={12}>
                        <Form.Item
                            label="副主题"
                            name="topicEx"
                            tooltip="副主题，与主题组合唯一约束。
                            用于区分同一主题下的不同子类，例如：act , 首尾不能是 “/”，可留空。"
                            rules={[
                                {
                                    validator: (_: any, value: string) => {
                                        if (!value) return Promise.resolve();
                                        if (value.startsWith('/') || value.endsWith('/')) {
                                            return Promise.reject(new Error('副主题首尾不能是 “/”'));
                                        }
                                        return Promise.resolve();
                                    }
                                }
                            ]}>
                            <EditableSelect
                                defaultService={defaultService}
                                apiConfig={{ apiId: 'topicExSelect' }}
                                placeholder="如：ESP32/S3（首尾不能是 /，可空）"
                                // 失焦时去掉首尾的 /
                                blurTransform={(v) => v.replace(/^\/+|\/+$/g, '')}
                            />
                        </Form.Item>
                    </Col>

                    <Col span={8}>
                        <Form.Item
                            label="质量"
                            name="qos"
                            tooltip="MQTT 服务质量等级（QoS）：0=最多一次（消息可能丢失，开销最小）；1=至少一次（保证送达，可能重复）；2=只有一次（不丢不重，开销最大）。">
                            <Select
                                defaultValue={1}
                                allowClear
                                placeholder="质量"
                                options={qosData}
                                showSearch={{
                                    optionFilterProp: 'children',
                                    filterOption: (input, option: any) =>
                                        option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                }} />
                        </Form.Item>
                    </Col>

                    <Col span={4}>
                        <Form.Item
                            label="可回复"
                            name="isReply"
                            valuePropName="checked"
                            initialValue={true}
                            tooltip="是否可回复主题。">
                            <Switch
                                checkedChildren="是"
                                unCheckedChildren="否" />
                        </Form.Item>
                    </Col>

                    <Col span={6}>
                        <Form.Item
                            label="排序"
                            name="sort"
                            tooltip="列表展示时的排序权重，数值越小越靠前。用于调整主题在列表中的显示顺序，默认 0。">
                            <InputNumber
                                style={{ width: 130 }} />
                        </Form.Item>
                    </Col>

                    <Col span={6}>
                        <Form.Item
                            label="状态"
                            name="isUse"
                            valuePropName="checked"
                            initialValue={true}
                            tooltip="主题是否启用。启用后平台可正常发布/订阅该主题；关闭后平台不再处理相关消息，可用于临时停用。">
                            <Switch
                                checkedChildren="启用"
                                unCheckedChildren="关闭" />
                        </Form.Item>
                    </Col>
                </Row>
            )
        }
    };

    return (<WebEditUniversal ref={ref} {...extendProps} />);
};

export default EditComponent;